/**
 * The tenant change channel (D35): "something with this id changed", and
 * nothing else.
 *
 *     resource.changed { kind, id, op, version, changedBy }
 *
 * **IDs only, never data.** A client that hears a change invalidates what it
 * holds of that resource and re-reads it over REST, where the caller's grants
 * are checked as for any read — so the channel needs no per-resource
 * permission filter, and a change to something the caller may not read tells
 * them only that an id they cannot open moved.
 *
 * **Emitted by the storage ports, not the routes** (`@acp/handlers/changes.js`):
 * every writer — REST, WS row patches (rundown and playlist heads; rows keep
 * their per-rundown channel), replication from the site link and the peer
 * link, migrations, cloud-production events — goes through the same stores,
 * so nothing can write without being heard. Bursts are coalesced per
 * request/invocation: one frame per resource, its last state.
 *
 * **Tenant-scoped.** A connection hears its own tenant's changes only — the
 * tenant comes from its verified token (D13), never from the frame.
 *
 * `version` is the resource's optimistic-concurrency version after the write
 * (a row's `version` for rundown/playlist heads) when it has one, else null.
 * `changedBy` is who made the write when a user did (`displayName`: their
 * email, else their user id); null for a machine writer — replication, a
 * migration, a cloud-production event.
 */
import { openEnum } from './openEnum.js';
import { z } from 'zod';

export const RESOURCE_KINDS = ['rundown', 'playlist', 'studio', 'showStyle', 'production', 'cloudProduction', 'site', 'node', 'user', 'group', 'tenant', 'announcement', 'releasePin', 'releaseRollout', 'releaseRing', 'alert', 'alertSettings', 'onboarding', 'graphicsProject', 'graphicsSettings', 'graphicsTemplate', 'graphicsTheme'] as const;
/** Closed: the kinds this build emits. */
const resourceKindWireSchema = z.enum(RESOURCE_KINDS);
export type ResourceKindWire = z.infer<typeof resourceKindWireSchema>;
/** Open (D37 §4.3): the kind a change frame carries — a newer server announces kinds an older SPA has no screen for. */
const resourceKindOpenWireSchema = openEnum(RESOURCE_KINDS);
export type ResourceKindOpenWire = z.infer<typeof resourceKindOpenWireSchema>;

const changeOpWireSchema = z.enum(['upsert', 'delete']);
export type ChangeOpWire = z.infer<typeof changeOpWireSchema>;

const changedByWireSchema = z.object({ userId: z.string().min(1).max(128), displayName: z.string().min(1).max(320) }).strict();
export type ChangedByWire = z.infer<typeof changedByWireSchema>;

export const resourceChangeWireSchema = z
  .object({
    kind: resourceKindOpenWireSchema,
    id: z.string().min(1).max(128),
    op: changeOpWireSchema,
    version: z.number().int().nonnegative().nullable(),
    changedBy: changedByWireSchema.nullable(),
  })
  .strict();
export type ResourceChangeWire = z.infer<typeof resourceChangeWireSchema>;
